import { phoneDigits } from './phoneFormat'
import { toYmd } from './calendarDate'

/**
 * My Account "Edit details" (specs/customer-sync, T9).
 *
 * The form starts from the member's Allcal row (orgStripeCustomers), not
 * from the Stripe customer. The save is one PUT /organization/:orgId/paper
 * that names the row by its _id and the login (allcalUserId), and carries
 * only the fields the member changed. A field the member emptied goes in
 * `clear` (email never does). Errors come back as HTTP 4xx/5xx with
 * { error: true, code, message }; a success may carry `warnings`.
 */

const isBlank = (v) => v === null || v === undefined || String(v).trim() === ''
const text = (v) => (typeof v === 'string' ? v.trim() : '')
const fullName = (first, last) => [first, last].map((s) => String(s || '').trim()).filter(Boolean).join(' ')

// The same check as the API's, plus no spaces inside (spec Decisions: "Email spaces").
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * The edit form's values from the Allcal row. Name and email fall back to
 * the Allcal login when the row has none; nothing comes from Stripe.
 */
export const memberFormValues = (row, login) => {
    const r = row || {}
    const l = login || {}
    return {
        nameInState: fullName(r.firstName, r.lastName) || text(r.name) || text(l.name),
        email: text(r.email) || text(l.email),
        phone: isBlank(r.phoneNumber) ? '' : phoneDigits(r.phoneNumber),
        address: text(r.address),
        mailingAddress: text(r.mailingAddress),
        dateOfBirth: isBlank(r.dateOfBirth) ? null : r.dateOfBirth,
        emergencyContactName: text(r.emergencyContactName),
        emergencyContact: isBlank(r.emergencyContact) ? '' : phoneDigits(r.emergencyContact)
    }
}


/**
 * What changed between the form's starting values and its current ones.
 * @return {{ fields: Object, clear: string[], errors: string[] }}
 *   fields: API field name → new value (changed fields only);
 *   clear: API field names the member emptied;
 *   errors: messages to show; nothing is sent while there are any.
 */
export const memberChanges = (initial, current) => {
    const fields = {}
    const clear = []
    const errors = []
    const before = initial || {}
    const after = current || {}

    const name = text(after.nameInState)
    if (!name) errors.push('Name cannot be empty')
    else if (name !== text(before.nameInState)) fields.name = name

    const email = text(after.email)
    if (!email) errors.push('Email cannot be empty')
    else if (/\s/.test(email)) errors.push('An email cannot contain spaces')
    else if (!EMAIL_RE.test(email)) errors.push('Please enter a valid email')
    else if (email !== text(before.email)) fields.email = email

    const phoneField = (key, apiKey, label) => {
        const raw = after[key]
        const digits = phoneDigits(raw)
        const was = phoneDigits(before[key])
        if (isBlank(raw) || digits === '') {
            if (was) clear.push(apiKey)
            return
        }
        if (digits.length !== 10) {
            errors.push(label + ' must be a 10-digit US number')
            return
        }
        if (digits !== was) fields[apiKey] = digits
    }
    phoneField('phone', 'phoneNumber', 'Phone')
    phoneField('emergencyContact', 'emergencyContact', 'Emergency contact phone')

    ;['address', 'mailingAddress', 'emergencyContactName'].forEach((k) => {
        const now = text(after[k])
        const was = text(before[k])
        if (!now) {
            if (was) clear.push(k)
        } else if (now !== was) {
            fields[k] = now
        }
    })

    // A calendar day, sent as "YYYY-MM-DD" (no time zone shift).
    const dob = toYmd(after.dateOfBirth)
    const dobWas = toYmd(before.dateOfBirth)
    if (!dob) {
        if (dobWas) clear.push('dateOfBirth')
    } else if (dob !== dobWas) {
        fields.dateOfBirth = dob
    }

    return { fields, clear, errors }
}

/** The API's message for a failed call (4xx/5xx body, or a 200 with error: true). */
export const apiFailure = (error) => {
    const res = error && error.response
    const body = (res && res.data) || (error && error.data) || {}
    return {
        status: res ? res.status : 0,
        code: body.code || null,
        message: (typeof body.message === 'string' && body.message) || null
    }
}

// Warnings a member can act on or should know about; the rest are for staff.
const MEMBER_WARNINGS = {
    'address-not-parsed': 'Saved. Your address could not be read as street, city, state and ZIP, so billing still has the previous one.',
    'login-email-failed': 'Saved. Your sign-in email did not change; please contact us to update it.'
}

/** The messages to show the member for a successful save's warnings. */
export const memberWarningMessages = (warnings) =>
    (Array.isArray(warnings) ? warnings : [])
        .map((w) => w && MEMBER_WARNINGS[w.code])
        .filter((m, i, all) => m && all.indexOf(m) === i)
